import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Breadcrumb, Button, Card, Divider, Form, Image, Input, Popconfirm, Table, Tag } from "antd";
import { useDispatch } from "react-redux";
import sliderService from "../../services/slider-service";
import templateContentService from "../../services/template-content-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import { createdColumnStatic, withCreatedColumn } from '../../shared/functional/created-sort';
import fileUtils from "../../utils/file-utils";
import AddEditSliderDialog from "./AddEditSliderDialog";
import { LINK_TYPE, groupType } from "../../constants/app-constants";

/**
 * NMU-482: shown in place of a thumbnail whose file cannot be loaded.
 *
 * Inlined as a data URI rather than an asset request: the point of a fallback is
 * to be the one image that cannot itself 404. It is only ever an `<img>` source,
 * where an SVG has no script access.
 */
const THUMBNAIL_FALLBACK =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="56" viewBox="0 0 120 56">
      <rect width="120" height="56" rx="6" fill="#f0f0f0" stroke="#d9d9d9"/>
      <path d="M38 36l10-12 8 10 6-6 20 20H38z" fill="#bfbfbf"/>
      <circle cx="45" cy="20" r="4" fill="#bfbfbf"/>
      <text x="60" y="52" font-size="8" fill="#8c8c8c" text-anchor="middle" font-family="sans-serif">ไม่พบรูปภาพ</text>
    </svg>`,
  );

/**
 * NMU-473: manage the homepage slider cards.
 *
 * The "เนื้อหาที่ผูกไว้" column resolves each card's templateId to the page's own
 * title, because a raw id tells an editor nothing about which page a card opens.
 * Titles are fetched once for the whole table rather than per row.
 */
const SliderManagement = () => {
  const dispatch = useDispatch();
  const [list, setList] = useState([]);
  const [keyword, setKeyword] = useState(null);
  const [contentTitles, setContentTitles] = useState({});
  const [isShowDialog, setIsShowDialog] = useState(false);
  const [editData, setEditData] = useState(null);

  /** Resolve the bound page titles for every CONTENT card in one pass. */
  const loadContentTitles = async (rows) => {
    const ids = Array.from(new Set(rows.map((r) => r.templateId).filter(Boolean)));
    if (!ids.length) return;
    const resolved = {};
    await Promise.all(
      ids.map(async (id) => {
        try {
          const resp = await templateContentService.getById({ id });
          const bean = resp && resp.bean;
          if (bean) {
            resolved[id] =
              (bean.mainTitle && (bean.mainTitle.titleTH || bean.mainTitle.titleEN)) || id;
          }
        } catch (err) {
          // A deleted page leaves the id visible rather than blanking the cell.
        }
      }),
    );
    setContentTitles((prev) => ({ ...prev, ...resolved }));
  };

  const doSearch = async (q) => {
    try {
      dispatch(appLoaderAction.enable());
      const rows = await sliderService.search({ q });
      setList(rows);
      await loadContentTitles(rows);
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  useEffect(() => {
    doSearch(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async (id) => {
    try {
      dispatch(appLoaderAction.enable());
      await sliderService.remove(id);
      notifyUtitls.success("สำเร็จ", "ลบข้อมูลเรียบร้อย");
      await doSearch(keyword);
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  /** Move a card one place and persist the whole resulting order. */
  const onMove = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setList(reordered);
    try {
      dispatch(appLoaderAction.enable());
      await sliderService.reorder(reordered.map((r) => r.sliderId));
      await doSearch(keyword);
    } catch (err) {
      notifyUtitls.error(err);
      await doSearch(keyword); // put the visible order back to what was saved
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  /*
    NMU-580: this table shows วันที่/เวลาสร้าง but does not sort by it.

    The row order here *is* the data — ลำดับ carries the move buttons and
    saving writes the sequence the rows are currently in. Re-ordering them by
    creation date would leave those buttons moving a สไลเดอร์ against a
    sequence nobody chose, and the next save would write that back over the
    arrangement. Flagged on the ticket.
  */
  const columns = [
    {
      title: "ลำดับ",
      key: "order",
      width: 130,
      render: (_, row, index) => (
        <div className="d-flex align-items-center">
          <span className="me-2">{row.seqNo || index + 1}</span>
          <Button size="small" className="me-1" disabled={index === 0} onClick={() => onMove(index, -1)}>
            <Icon.ChevronUp size={14} />
          </Button>
          <Button size="small" disabled={index === list.length - 1} onClick={() => onMove(index, 1)}>
            <Icon.ChevronDown size={14} />
          </Button>
        </div>
      ),
    },
    {
      title: "รูปภาพ",
      dataIndex: "attachImage",
      key: "attachImage",
      width: 150,
      render: (img, row) =>
        img && img.originalFilename ? (
          <Image
            src={fileUtils.getFileRenderUrl(groupType.SLIDER, row.sliderId, img.originalFilename)}
            alt={(row.title && row.title.titleTH) || "slider"}
            width={120}
            height={56}
            style={{ objectFit: "cover", borderRadius: 6 }}
            // NMU-482: a card whose file is missing must read as "no image", not
            // as the browser's broken-image glyph plus a wall of alt text — that
            // is what the whole column looked like before the poster was
            // actually being saved. antd's Image swaps in this placeholder on the
            // img element's own error event, which also covers a deleted file.
            fallback={THUMBNAIL_FALLBACK}
            preview={{ mask: "ดูรูปเต็ม" }}
          />
        ) : (
          <span className="text-muted">ไม่มีรูปภาพ</span>
        ),
    },
    {
      title: "ชื่อหัวข้อสไลเดอร์",
      key: "title",
      render: (_, row) => (row.title && (row.title.titleTH || row.title.titleEN)) || "-",
    },
    {
      title: "เนื้อหาที่ผูกไว้",
      key: "linked",
      render: (_, row) =>
        row.linkType === LINK_TYPE.CONTENT
          ? contentTitles[row.templateId] || row.templateId || "-"
          : row.url || "-",
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => (status ? <Tag color="green">Online</Tag> : <Tag>Offline</Tag>),
    },
    {
      title: "ดำเนินการ",
      key: "action",
      width: 130,
      render: (_, row) => (
        <div className="d-flex">
          <label
            className="pe-3 border-end"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setEditData(row);
              setIsShowDialog(true);
            }}
          >
            <Icon.Edit size={18} />
          </label>
          <Popconfirm title="ยืนยันการลบข้อมูล ?" onConfirm={() => onDelete(row.sliderId)}>
            <label className="ps-3" style={{ cursor: "pointer" }}>
              <Icon.Trash2 size={18} />
            </label>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h2 className="color-primary-title">จัดการสไลเดอร์</h2>
      <Breadcrumb>
        <Breadcrumb.Item>การจัดการเนื้อหา</Breadcrumb.Item>
        <Breadcrumb.Item>จัดการสไลเดอร์</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />

      <div className="d-flex justify-content-between">
        <Button
          type="primary"
          onClick={() => {
            setEditData(null);
            setIsShowDialog(true);
          }}
        >
          <Icon.Plus className="me-1" size={18} /> เพิ่มสไลเดอร์
        </Button>
      </div>

      <Card className="mt-4" bordered={false}>
        <Form layout="vertical" className="mb-3">
          <Form.Item label="ค้นหา (ชื่อหัวข้อ / ป้ายกำกับ)" className="mb-0" style={{ maxWidth: 360 }}>
            <Input.Search
              allowClear
              onSearch={(value) => {
                setKeyword(value || null);
                doSearch(value || null);
              }}
            />
          </Form.Item>
        </Form>

        <Table rowKey={(row) => row.sliderId} columns={withCreatedColumn(columns, createdColumnStatic)} dataSource={list} pagination={false} />
      </Card>

      <AddEditSliderDialog
        isShow={isShowDialog}
        editData={editData}
        onCancel={() => setIsShowDialog(false)}
        onSaveComplete={() => {
          setIsShowDialog(false);
          doSearch(keyword);
        }}
      />
    </div>
  );
};

export default SliderManagement;
